import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTopicDetails, deleteTopic } from '../services/topics';
import { useToast } from '../hooks/useToast';
import { setTitle } from '../utils/environment';
import { stripMarkdownFormatting } from '../utils/markdown';
import Pagination from '../components/Pagination';
import useNavigation from '../hooks/useNavigation';

const TopicDetail = () => {
  const { topicId } = useParams();
  const { navigateToArticle, goBack } = useNavigation();
  const { showSuccess, showError } = useToast();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(() => {
    const savedToggle = sessionStorage.getItem(`topic-${topicId}-showAll`);
    return savedToggle === 'true';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(null);


  const fetchTopicDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch articles with pagination
      const topicData = await getTopicDetails(topicId, {
        unread_only: !showAll,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      });
      setTopic(topicData);
      setPagination(topicData.pagination);
      setTitle(`${topicData.label} - Topics`);
    } catch (err) {
      console.error('Failed to fetch topic details:', err);
      setError(err.message);
      setTitle('Topic Not Found');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTopic = async () => {
    if (!window.confirm(`Are you sure you want to delete the topic "${topic?.label}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);
      await deleteTopic(topicId);
      showSuccess('Topic deleted successfully');
      goBack(); // Go back to previous page
    } catch (err) {
      console.error('Failed to delete topic:', err);
      showError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleArticleClick = (articleId) => {
    navigateToArticle(articleId, {
      context: {
        from: 'topic',
        topicId: topicId,
        topicLabel: topic?.label
      },
      pageState: {
        showAll: showAll,
        currentPage: currentPage,
        pageSize: pageSize
      },
      preserveHistory: true
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (perPage) => {
    setPageSize(perPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatSimilarity = (similarity) => {
    return Math.round(similarity * 100);
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 0.9) return 'text-green-600 bg-green-50';
    if (similarity >= 0.8) return 'text-green-600 bg-green-50';
    if (similarity >= 0.7) return 'text-blue-600 bg-blue-50';
    if (similarity >= 0.6) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  useEffect(() => {
    if (topicId) {
      fetchTopicDetails();
    }
  }, [topicId, currentPage, pageSize, showAll]);

  // Handle refresh when returning from article navigation
  useEffect(() => {
    if (location.state?.refreshData) {
      fetchTopicDetails();
    }
  }, [location.state?.refreshData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-600 border-t-transparent"></div>
            <span className="ml-4 text-lg text-gray-600">Loading topic details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !topic) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Topic Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'The topic you\'re looking for doesn\'t exist.'}</p>
            <Link
              to="/topics"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              ← Back to Topics
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // No need for client-side filtering since backend handles it with unread_only parameter
  const filteredArticles = topic.articles;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-gray-700">Dashboard</Link>
            <span className="mx-2">→</span>
            <Link to="/topics" className="hover:text-gray-700">Topics</Link>
            <span className="mx-2">→</span>
            <span className="text-gray-800">{topic.label}</span>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  {topic.label}
                </h1>
                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <span>📄 {topic.unread_count} unread</span>
                  <span>📚 {topic.article_count} total articles</span>
                  <span>🗓️ Created {formatDate(topic.created_at)}</span>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={handleDeleteTopic}
                  disabled={deleting}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deleting ? 'Deleting...' : '🗑️ Delete Topic'}
                </button>
              </div>
            </div>

            {/* Topic Metadata */}
            {topic.topic_metadata && (
              <div className="mt-4 p-4 bg-gray-50 rounded-md">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Algorithm:</span>
                    <div className="text-gray-600">{topic.algorithm}</div>
                  </div>
                  {topic.topic_metadata.silhouette_score && (
                    <div>
                      <span className="font-medium text-gray-700">Quality Score:</span>
                      <div className="text-gray-600">
                        {(topic.topic_metadata.silhouette_score * 100).toFixed(1)}%
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-gray-700">Cluster Size:</span>
                    <div className="text-gray-600">{topic.topic_metadata.article_count}</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Generation:</span>
                    <div className="text-gray-600 capitalize">
                      {topic.topic_metadata.generated_from || 'Unknown'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter Controls */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setShowAll(checked);
                    sessionStorage.setItem(`topic-${topicId}-showAll`, checked.toString());
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Show all</span>
              </label>
            </div>
            <div className="text-sm text-gray-500">
              {pagination ?
                `Showing ${Math.min((pagination.page - 1) * pagination.per_page + 1, pagination.total)} - ${Math.min(pagination.page * pagination.per_page, pagination.total)} of ${pagination.total} articles` :
                `${filteredArticles.length} articles`
              }
            </div>
          </div>
        </div>

        {/* Articles List */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Articles in Topic</h2>
            <p className="text-gray-600 text-sm mt-1">
              Articles are ordered by similarity to the topic cluster center
            </p>
          </div>

          {filteredArticles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-4">📰</div>
              <p className="text-lg font-medium">No articles found</p>
              <p className="text-sm mt-1">
                {showAll
                  ? 'This topic has no articles'
                  : 'No unread articles in this topic'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredArticles.map((article) => (
                <div
                  key={article.id}
                  onClick={() => handleArticleClick(article.id)}
                  className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-medium text-gray-800 leading-6 mr-4">
                          {article.title}
                        </h3>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSimilarityColor(article.similarity)}`}>
                            {formatSimilarity(article.similarity)}% match
                          </span>
                          {!article.is_read && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Unread
                            </span>
                          )}
                        </div>
                      </div>

                      {article.summary && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {stripMarkdownFormatting(article.summary)}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-4">
                          {article.author && (
                            <span>👤 {article.author}</span>
                          )}
                          {article.domain && (
                            <span>🌐 {article.domain}</span>
                          )}
                        </div>
                        <span>📅 {formatDate(article.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            totalItems={pagination.total}
            itemsPerPage={pagination.per_page}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            isLoading={loading}
            className="mt-8"
          />
        )}
      </div>
    </div>
  );
};

export default TopicDetail;